import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { useSortContext, PROJECT_LIST } from "./SortContext";

/**
 * ProjectNavigation - Adds next and previous buttons to project pages.
 * Navigates in the same order as the current sort/filter on the home page.
 * @param {string} currentProject - The URL path of the current project
 */
const ProjectNavigation = ({ currentProject }) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { selectedOption, selectedCategory } = useSortContext();
  const [prevHovered, setPrevHovered] = useState(false);
  const [nextHovered, setNextHovered] = useState(false);

  // Mirror the same sort + filter logic as ImageGrid
  const projects = React.useMemo(() => {
    const filtered = selectedCategory
      ? PROJECT_LIST.filter((p) => p.categories.includes(selectedCategory))
      : PROJECT_LIST;
    return [...filtered].sort((a, b) => {
      if (selectedOption === "") {
        return (a.order ?? 999) - (b.order ?? 999);
      } else if (selectedOption === "oldToNew") {
        return new Date(a.date) - new Date(b.date);
      }
      return new Date(b.date) - new Date(a.date);
    });
  }, [selectedOption, selectedCategory]);

  // Find the index of the current project
  const currentIndex = projects.findIndex(project => project.path === currentProject);
  
  // If not found (e.g. filtered out by category), fall back to unfiltered default order
  const effectiveProjects = currentIndex === -1
    ? [...PROJECT_LIST].sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
    : projects;
  const effectiveIndex = currentIndex === -1
    ? effectiveProjects.findIndex(p => p.path === currentProject)
    : currentIndex;
  
  // If not found in either list, don't render anything
  if (effectiveIndex === -1) return null;
  
  // Calculate previous and next indices
  const prevIndex = effectiveIndex > 0 ? effectiveIndex - 1 : effectiveProjects.length - 1;
  const nextIndex = effectiveIndex < effectiveProjects.length - 1 ? effectiveIndex + 1 : 0;
  
  const prevProject = effectiveProjects[prevIndex];
  const nextProject = effectiveProjects[nextIndex];

  const segmentBase = {
    textDecoration: "none",
    fontFamily: "Fanwood, serif",
    fontSize: isMobile ? "11px" : "13px",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    padding: isMobile ? "10px 16px" : "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: isMobile ? "8px" : "10px",
    transition: "background-color 0.2s ease, color 0.2s ease",
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: isMobile ? "16px 12px" : "24px 20px",
      }}
    >
      {/* PREV pill */}
      <Link
        to={prevProject.path}
        onMouseEnter={() => setPrevHovered(true)}
        onMouseLeave={() => setPrevHovered(false)}
        style={{
          ...segmentBase,
          color: prevHovered ? "white" : "black",
          backgroundColor: prevHovered ? "black" : "transparent",
          border: "1.5px solid black",
          borderRadius: "999px",
        }}
      >
        <span>←</span>
        <span style={{ maxWidth: isMobile ? "120px" : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMobile ? "nowrap" : "normal" }}>
          {prevProject.name}
        </span>
      </Link>

      {/* NEXT pill */}
      <Link
        to={nextProject.path}
        onMouseEnter={() => setNextHovered(true)}
        onMouseLeave={() => setNextHovered(false)}
        style={{
          ...segmentBase,
          color: nextHovered ? "white" : "black",
          backgroundColor: nextHovered ? "black" : "transparent",
          border: "1.5px solid black",
          borderRadius: "999px",
          textAlign: "right",
        }}
      >
        <span style={{ maxWidth: isMobile ? "120px" : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMobile ? "nowrap" : "normal" }}>
          {nextProject.name}
        </span>
        <span>→</span>
      </Link>
    </div>
  );
};

export default ProjectNavigation;